import React, { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  Divider,
  Paper,
  alpha,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Receipt as TaxIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { format } from "date-fns";
import { companyAPI, CompanyDto } from "../../services/companyAPI";
import { useAuthStore } from "../../stores/authStore";
import PageHeader from "../../components/layout/PageHeader";
import DataListCard from "../../components/layout/DataListCard";
import TableActionButton from "../../components/layout/TableActionButton";
import { tokens } from "../../theme";

type StatusFilter = "all" | "active" | "inactive";

const initialCreateForm = {
  name: "",
  code: "",
  email: "",
  phone: "",
  address: "",
  businessType: "ALL_IN_ONE",
  dlNumber: "",
  fssaiNumber: "",
  gstin: "",
  pan: "",
  stateCode: "",
  adminName: "",
  adminEmail: "",
  adminPassword: "",
  adminPhone: "",
};

const CompaniesPage: React.FC = () => {
  const user = useAuthStore((s) => s.user);
  const isPlatform = String(user?.role || "").toUpperCase() === "PLATFORM_ADMIN";

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

  const [rows, setRows] = useState<CompanyDto[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  // Dialog states
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(initialCreateForm);
  const [editCompany, setEditCompany] = useState<CompanyDto | null>(null);
  const [viewCompany, setViewCompany] = useState<CompanyDto | null>(null);
  const [deleteCompany, setDeleteCompany] = useState<CompanyDto | null>(null);

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const loadData = useCallback(async () => {
    if (!isPlatform) return;
    setLoading(true);
    try {
      const res = await companyAPI.list({
        pageNo: page + 1,
        pageSize: rowsPerPage,
        search: debouncedSearch || undefined,
        isActive:
          statusFilter === "active"
            ? true
            : statusFilter === "inactive"
            ? false
            : undefined,
      });
      setRows(res.data);
      setTotalCount(res.total);
    } catch (err: any) {
      setAlert({
        type: "error",
        message:
          err.response?.data?.message ||
          err.message ||
          "Failed to load companies",
      });
    } finally {
      setLoading(false);
    }
  }, [isPlatform, page, rowsPerPage, debouncedSearch, statusFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (!isPlatform) {
    return <Navigate to="/dashboard" replace />;
  }

  // Handle Create Submit
  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createForm.name.trim()) {
      setAlert({ type: "error", message: "Company name is required" });
      return;
    }

    setSaving(true);
    try {
      await companyAPI.create({
        name: createForm.name.trim(),
        code: createForm.code.trim().toUpperCase() || undefined,
        email: createForm.email.trim() || undefined,
        phone: createForm.phone.trim() || undefined,
        address: createForm.address.trim() || undefined,
        businessType: createForm.businessType || "ALL_IN_ONE",
        dlNumber: createForm.dlNumber.trim() || undefined,
        fssaiNumber: createForm.fssaiNumber.trim() || undefined,
        gstin: createForm.gstin.trim() || undefined,
        pan: createForm.pan.trim() || undefined,
        stateCode: createForm.stateCode.trim() || undefined,
        adminName: createForm.adminName.trim() || undefined,
        adminEmail: createForm.adminEmail.trim() || undefined,
        adminPassword: createForm.adminPassword || undefined,
        adminPhone: createForm.adminPhone.trim() || undefined,
      });

      setCreateOpen(false);
      setCreateForm(initialCreateForm);
      setAlert({
        type: "success",
        message: "Company created successfully!",
      });
      loadData();
    } catch (err: any) {
      setAlert({
        type: "error",
        message:
          err.response?.data?.message ||
          err.message ||
          "Failed to create company",
      });
    } finally {
      setSaving(false);
    }
  };

  // Handle Edit Submit
  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editCompany) return;
    if (!editCompany.name?.trim()) {
      setAlert({ type: "error", message: "Company name is required" });
      return;
    }

    setSaving(true);
    try {
      await companyAPI.update(editCompany.id, {
        name: editCompany.name.trim(),
        code: editCompany.code?.trim().toUpperCase(),
        email: editCompany.email?.trim() || null,
        phone: editCompany.phone?.trim() || null,
        address: editCompany.address?.trim() || null,
        businessType: editCompany.businessType || "ALL_IN_ONE",
        dlNumber: editCompany.dlNumber?.trim() || null,
        fssaiNumber: editCompany.fssaiNumber?.trim() || null,
        gstin: editCompany.gstin?.trim() || null,
        pan: editCompany.pan?.trim() || null,
        stateCode: editCompany.stateCode?.trim() || null,
        isActive: editCompany.isActive,
      });

      setEditCompany(null);
      setAlert({
        type: "success",
        message: "Company updated successfully!",
      });
      loadData();
    } catch (err: any) {
      setAlert({
        type: "error",
        message:
          err.response?.data?.message ||
          err.message ||
          "Failed to update company",
      });
    } finally {
      setSaving(false);
    }
  };

  // Handle Delete (Deactivate)
  const handleDeleteConfirm = async () => {
    if (!deleteCompany) return;
    setDeleting(true);
    try {
      await companyAPI.delete(deleteCompany.id);
      setDeleteCompany(null);
      setAlert({
        type: "success",
        message: `Company "${deleteCompany.name}" deactivated successfully`,
      });
      loadData();
    } catch (err: any) {
      setAlert({
        type: "error",
        message:
          err.response?.data?.message ||
          err.message ||
          "Failed to deactivate company",
      });
    } finally {
      setDeleting(false);
    }
  };

  const statusChip = (active?: boolean) => (
    <Chip
      label={active ? "Active" : "Inactive"}
      size="small"
      sx={{
        fontWeight: 600,
        fontSize: "0.75rem",
        bgcolor: active ? tokens.colors.success.light : tokens.colors.error.light,
        color: active ? tokens.colors.success.main : tokens.colors.error.main,
        border: "none",
      }}
    />
  );

  const filterDropdowns = (
    <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 140 } }}>
      <InputLabel>Status</InputLabel>
      <Select
        value={statusFilter}
        label="Status"
        onChange={(e) => {
          setStatusFilter(e.target.value as StatusFilter);
          setPage(0);
        }}
      >
        <MenuItem value="all">All Status</MenuItem>
        <MenuItem value="active">Active</MenuItem>
        <MenuItem value="inactive">Inactive</MenuItem>
      </Select>
    </FormControl>
  );

  return (
    <Box>
      <PageHeader
        title="Companies"
        subtitle="Manage tenant organizations, registration codes, and super admins"
        breadcrumbs={[
          { label: "Dashboard", to: "/dashboard" },
          { label: "Companies" },
        ]}
        action={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
            sx={{
              boxShadow: "none",
              "&:hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.15)" },
            }}
          >
            Create Company
          </Button>
        }
      />

      {alert && (
        <Alert
          severity={alert.type}
          sx={{ mb: 2 }}
          onClose={() => setAlert(null)}
        >
          {alert.message}
        </Alert>
      )}

      <DataListCard
        searchPlaceholder="Search by name, code, email or GSTIN…"
        searchValue={search}
        onSearchChange={(v) => {
          setSearch(v);
        }}
        filters={filterDropdowns}
        footer={
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            sx={{ width: "100%" }}
          />
        }
      >
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={8}>
            <CircularProgress size={32} />
            <Typography color="text.secondary" ml={2}>
              Loading companies…
            </Typography>
          </Box>
        ) : rows.length === 0 ? (
          <Box display="flex" flexDirection="column" alignItems="center" py={8}>
            <BusinessIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
            <Typography variant="h6" color="text.secondary">
              No companies found
            </Typography>
            <Typography variant="body2" color="text.disabled">
              {debouncedSearch
                ? "Try adjusting your search criteria"
                : "Click 'Create Company' to add your first tenant organization"}
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small" sx={{ width: "100%" }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Company Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Contact Info</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>GSTIN / PAN</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Created Date</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((company) => (
                  <TableRow key={company.id} hover>
                    <TableCell>
                      <Chip
                        label={company.code || "—"}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          bgcolor: alpha(tokens.colors.primary.main, 0.1),
                          color: "primary.main",
                          borderRadius: tokens.radius.sm,
                          letterSpacing: "0.5px",
                        }}
                      />
                    </TableCell>

                    <TableCell>
                      <Box>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color="text.primary"
                        >
                          {company.name}
                        </Typography>
                        {company.address ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                            sx={{ display: "flex", alignItems: "center", gap: 0.5, maxWidth: 240 }}
                          >
                            <LocationIcon sx={{ fontSize: 13, flexShrink: 0 }} />
                            {company.address}
                          </Typography>
                        ) : null}
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Stack spacing={0.5}>
                        {company.email ? (
                          <Typography
                            variant="caption"
                            color="text.primary"
                            display="flex"
                            alignItems="center"
                            gap={0.5}
                          >
                            <EmailIcon sx={{ fontSize: 13, color: "text.secondary" }} />
                            {company.email}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.disabled">
                            No email
                          </Typography>
                        )}
                        {company.phone ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="flex"
                            alignItems="center"
                            gap={0.5}
                          >
                            <PhoneIcon sx={{ fontSize: 13 }} />
                            {company.phone}
                          </Typography>
                        ) : null}
                      </Stack>
                    </TableCell>

                    <TableCell>
                      <Stack spacing={0.25}>
                        <Typography variant="body2" fontFamily="monospace">
                          {company.gstin || "—"}
                        </Typography>
                        {company.pan ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            fontFamily="monospace"
                          >
                            PAN: {company.pan}
                          </Typography>
                        ) : null}
                      </Stack>
                    </TableCell>

                    <TableCell>{statusChip(company.isActive)}</TableCell>

                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {company.createdAt
                          ? format(new Date(company.createdAt), "dd MMM yyyy")
                          : "—"}
                      </Typography>
                    </TableCell>

                    <TableCell align="right">
                      <Stack
                        direction="row"
                        spacing={0.5}
                        justifyContent="flex-end"
                      >
                        <TableActionButton
                          title="View Details"
                          variant="default"
                          onClick={() => setViewCompany(company)}
                        >
                          <ViewIcon fontSize="small" />
                        </TableActionButton>
                        <TableActionButton
                          title="Edit Company"
                          variant="primary"
                          onClick={() => setEditCompany({ ...company })}
                        >
                          <EditIcon fontSize="small" />
                        </TableActionButton>
                        <TableActionButton
                          title="Deactivate Company"
                          variant="danger"
                          onClick={() => setDeleteCompany(company)}
                        >
                          <DeleteIcon fontSize="small" />
                        </TableActionButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DataListCard>

      {/* CREATE COMPANY DIALOG */}
      <Dialog
        open={createOpen}
        onClose={() => !saving && setCreateOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: { borderRadius: tokens.radius.md },
        }}
      >
        <DialogTitle sx={{ pb: 1, borderBottom: "1px solid", borderColor: "divider" }}>
          <Typography variant="h6" fontWeight={700}>
            Create Tenant Company
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Set up organizational profile, tax IDs, and optional initial Super Admin
          </Typography>
        </DialogTitle>
        <form onSubmit={handleCreateSubmit}>
          <DialogContent sx={{ py: 3 }}>
            <Stack spacing={3}>
              {/* Section 1: Basic details */}
              <Box>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  color="primary.main"
                  gutterBottom
                  display="flex"
                  alignItems="center"
                  gap={0.5}
                >
                  <BusinessIcon fontSize="small" /> Organization Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={8}>
                    <TextField
                      label="Company Name"
                      placeholder="e.g. DSS Enterprises"
                      required
                      fullWidth
                      size="small"
                      value={createForm.name}
                      onChange={(e) =>
                        setCreateForm((f) => ({ ...f, name: e.target.value }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Company Code"
                      placeholder="e.g. DSS"
                      fullWidth
                      size="small"
                      value={createForm.code}
                      onChange={(e) =>
                        setCreateForm((f) => ({
                          ...f,
                          code: e.target.value.toUpperCase(),
                        }))
                      }
                      helperText="Unique uppercase identifier"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Official Email"
                      placeholder="info@company.com"
                      fullWidth
                      size="small"
                      type="email"
                      value={createForm.email}
                      onChange={(e) =>
                        setCreateForm((f) => ({ ...f, email: e.target.value }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Phone Number"
                      placeholder="+91 98765 43210"
                      fullWidth
                      size="small"
                      value={createForm.phone}
                      onChange={(e) =>
                        setCreateForm((f) => ({ ...f, phone: e.target.value }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Registered Address"
                      placeholder="Full company address..."
                      fullWidth
                      size="small"
                      multiline
                      rows={2}
                      value={createForm.address}
                      onChange={(e) =>
                        setCreateForm((f) => ({ ...f, address: e.target.value }))
                      }
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              {/* Section 2: Tax & Compliance */}
              <Box>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  color="primary.main"
                  gutterBottom
                  display="flex"
                  alignItems="center"
                  gap={0.5}
                >
                  <TaxIcon fontSize="small" /> Trade Vertical & Statutory Licenses
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="create-biz-type-label">Trade Vertical</InputLabel>
                      <Select
                        labelId="create-biz-type-label"
                        label="Trade Vertical"
                        value={createForm.businessType || "ALL_IN_ONE"}
                        onChange={(e) =>
                          setCreateForm((f) => ({
                            ...f,
                            businessType: e.target.value,
                          }))
                        }
                      >
                        <MenuItem value="ALL_IN_ONE">All-in-One / General</MenuItem>
                        <MenuItem value="RESTAURANT">Restaurant / Cafe</MenuItem>
                        <MenuItem value="GROCERY">Grocery / Supermarket</MenuItem>
                        <MenuItem value="PHARMACY">Pharmacy / Medical</MenuItem>
                        <MenuItem value="WHOLESALE">Wholesale / Trade</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Drug License No. (D.L. No.)"
                      placeholder="e.g. 20B/21B-MH-12345"
                      fullWidth
                      size="small"
                      value={createForm.dlNumber}
                      onChange={(e) =>
                        setCreateForm((f) => ({
                          ...f,
                          dlNumber: e.target.value,
                        }))
                      }
                      helperText="For Pharmacy / Chemist shops"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="FSSAI License No."
                      placeholder="e.g. 10019022009876"
                      fullWidth
                      size="small"
                      value={createForm.fssaiNumber}
                      onChange={(e) =>
                        setCreateForm((f) => ({
                          ...f,
                          fssaiNumber: e.target.value,
                        }))
                      }
                      helperText="For Food & Grocery businesses"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="GSTIN"
                      placeholder="22AAAAA0000A1Z5"
                      fullWidth
                      size="small"
                      value={createForm.gstin}
                      onChange={(e) =>
                        setCreateForm((f) => ({
                          ...f,
                          gstin: e.target.value.toUpperCase(),
                        }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="PAN"
                      placeholder="AAAAA0000A"
                      fullWidth
                      size="small"
                      value={createForm.pan}
                      onChange={(e) =>
                        setCreateForm((f) => ({
                          ...f,
                          pan: e.target.value.toUpperCase(),
                        }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="State Code"
                      placeholder="e.g. 07"
                      fullWidth
                      size="small"
                      value={createForm.stateCode}
                      onChange={(e) =>
                        setCreateForm((f) => ({
                          ...f,
                          stateCode: e.target.value,
                        }))
                      }
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider />

              {/* Section 3: Optional Admin */}
              <Box>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  color="primary.main"
                  gutterBottom
                  display="flex"
                  alignItems="center"
                  gap={0.5}
                >
                  <PersonIcon fontSize="small" /> Initial Super Admin (Optional)
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                  You can automatically provision a Super Admin account for this company.
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Admin Name"
                      placeholder="e.g. Rajesh Kumar"
                      fullWidth
                      size="small"
                      value={createForm.adminName}
                      onChange={(e) =>
                        setCreateForm((f) => ({ ...f, adminName: e.target.value }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Admin Email"
                      placeholder="admin@company.com"
                      fullWidth
                      size="small"
                      type="email"
                      value={createForm.adminEmail}
                      onChange={(e) =>
                        setCreateForm((f) => ({ ...f, adminEmail: e.target.value }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Admin Password"
                      type="password"
                      placeholder="Secure password"
                      fullWidth
                      size="small"
                      value={createForm.adminPassword}
                      onChange={(e) =>
                        setCreateForm((f) => ({
                          ...f,
                          adminPassword: e.target.value,
                        }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Admin Phone"
                      placeholder="+91 98765 43210"
                      fullWidth
                      size="small"
                      value={createForm.adminPhone}
                      onChange={(e) =>
                        setCreateForm((f) => ({ ...f, adminPhone: e.target.value }))
                      }
                    />
                  </Grid>
                </Grid>
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions
            sx={{ px: 3, py: 2, borderTop: "1px solid", borderColor: "divider" }}
          >
            <Button onClick={() => setCreateOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving || !createForm.name.trim()}
              startIcon={
                saving ? <CircularProgress size={18} color="inherit" /> : <AddIcon />
              }
            >
              Create Company
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* EDIT COMPANY DIALOG */}
      <Dialog
        open={Boolean(editCompany)}
        onClose={() => !saving && setEditCompany(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: { borderRadius: tokens.radius.md },
        }}
      >
        <DialogTitle sx={{ pb: 1, borderBottom: "1px solid", borderColor: "divider" }}>
          <Typography variant="h6" fontWeight={700}>
            Edit Company
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Update tenant details, contact, and tax credentials
          </Typography>
        </DialogTitle>
        {editCompany && (
          <form onSubmit={handleEditSubmit}>
            <DialogContent sx={{ py: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={8}>
                  <TextField
                    label="Company Name"
                    required
                    fullWidth
                    size="small"
                    value={editCompany.name}
                    onChange={(e) =>
                      setEditCompany({ ...editCompany, name: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Code"
                    fullWidth
                    size="small"
                    value={editCompany.code || ""}
                    onChange={(e) =>
                      setEditCompany({
                        ...editCompany,
                        code: e.target.value.toUpperCase(),
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Email"
                    fullWidth
                    size="small"
                    type="email"
                    value={editCompany.email || ""}
                    onChange={(e) =>
                      setEditCompany({ ...editCompany, email: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Phone"
                    fullWidth
                    size="small"
                    value={editCompany.phone || ""}
                    onChange={(e) =>
                      setEditCompany({ ...editCompany, phone: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Address"
                    fullWidth
                    size="small"
                    multiline
                    rows={2}
                    value={editCompany.address || ""}
                    onChange={(e) =>
                      setEditCompany({ ...editCompany, address: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="edit-biz-type-label">Trade Vertical</InputLabel>
                    <Select
                      labelId="edit-biz-type-label"
                      label="Trade Vertical"
                      value={editCompany.businessType || "ALL_IN_ONE"}
                      onChange={(e) =>
                        setEditCompany({
                          ...editCompany,
                          businessType: e.target.value,
                        })
                      }
                    >
                      <MenuItem value="ALL_IN_ONE">All-in-One / General</MenuItem>
                      <MenuItem value="RESTAURANT">Restaurant / Cafe</MenuItem>
                      <MenuItem value="GROCERY">Grocery / Supermarket</MenuItem>
                      <MenuItem value="PHARMACY">Pharmacy / Medical</MenuItem>
                      <MenuItem value="WHOLESALE">Wholesale / Trade</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Drug License No. (D.L. No.)"
                    placeholder="e.g. 20B/21B-MH-12345"
                    fullWidth
                    size="small"
                    value={editCompany.dlNumber || ""}
                    onChange={(e) =>
                      setEditCompany({
                        ...editCompany,
                        dlNumber: e.target.value,
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="FSSAI License No."
                    placeholder="e.g. 10019022009876"
                    fullWidth
                    size="small"
                    value={editCompany.fssaiNumber || ""}
                    onChange={(e) =>
                      setEditCompany({
                        ...editCompany,
                        fssaiNumber: e.target.value,
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="GSTIN"
                    fullWidth
                    size="small"
                    value={editCompany.gstin || ""}
                    onChange={(e) =>
                      setEditCompany({
                        ...editCompany,
                        gstin: e.target.value.toUpperCase(),
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="PAN"
                    fullWidth
                    size="small"
                    value={editCompany.pan || ""}
                    onChange={(e) =>
                      setEditCompany({
                        ...editCompany,
                        pan: e.target.value.toUpperCase(),
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="State Code"
                    fullWidth
                    size="small"
                    value={editCompany.stateCode || ""}
                    onChange={(e) =>
                      setEditCompany({
                        ...editCompany,
                        stateCode: e.target.value,
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(editCompany.isActive)}
                        onChange={(e) =>
                          setEditCompany({
                            ...editCompany,
                            isActive: e.target.checked,
                          })
                        }
                        color="success"
                      />
                    }
                    label={
                      <Typography variant="body2" fontWeight={600}>
                        Active Status
                      </Typography>
                    }
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions
              sx={{ px: 3, py: 2, borderTop: "1px solid", borderColor: "divider" }}
            >
              <Button onClick={() => setEditCompany(null)} disabled={saving}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={saving || !editCompany.name.trim()}
                startIcon={
                  saving ? <CircularProgress size={18} color="inherit" /> : undefined
                }
              >
                Save Changes
              </Button>
            </DialogActions>
          </form>
        )}
      </Dialog>

      {/* VIEW COMPANY DETAILS DIALOG */}
      <Dialog
        open={Boolean(viewCompany)}
        onClose={() => setViewCompany(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: { borderRadius: tokens.radius.md },
        }}
      >
        <DialogTitle sx={{ pb: 1, borderBottom: "1px solid", borderColor: "divider" }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6" fontWeight={700}>
              Company Details
            </Typography>
            {statusChip(viewCompany?.isActive)}
          </Stack>
        </DialogTitle>
        {viewCompany && (
          <DialogContent sx={{ py: 3 }}>
            <Stack spacing={2.5}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  bgcolor: "background.default",
                  borderRadius: tokens.radius.sm,
                }}
              >
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: tokens.radius.sm,
                      bgcolor: alpha(tokens.colors.primary.main, 0.1),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "primary.main",
                    }}
                  >
                    <BusinessIcon />
                  </Box>
                  <Box flex={1}>
                    <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                      {viewCompany.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      color="primary.main"
                    >
                      CODE: {viewCompany.code || "NOT SET"}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Email Address
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {viewCompany.email || "—"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Phone Number
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {viewCompany.phone || "—"}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Registered Address
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {viewCompany.address || "—"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Trade Vertical
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color="primary.main">
                    {viewCompany.businessType || "ALL_IN_ONE"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    D.L. No. (Pharmacy)
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {viewCompany.dlNumber || "—"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    FSSAI No.
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {viewCompany.fssaiNumber || "—"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    GSTIN
                  </Typography>
                  <Typography variant="body2" fontWeight={600} fontFamily="monospace">
                    {viewCompany.gstin || "—"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    PAN
                  </Typography>
                  <Typography variant="body2" fontWeight={600} fontFamily="monospace">
                    {viewCompany.pan || "—"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    State Code
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {viewCompany.stateCode || "—"}
                  </Typography>
                </Grid>
                {viewCompany.createdAt && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">
                      Created On
                    </Typography>
                    <Typography variant="body2">
                      {format(new Date(viewCompany.createdAt), "dd MMM yyyy, hh:mm a")}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Stack>
          </DialogContent>
        )}
        <DialogActions
          sx={{ px: 3, py: 2, borderTop: "1px solid", borderColor: "divider" }}
        >
          <Button onClick={() => setViewCompany(null)}>Close</Button>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => {
              const toEdit = viewCompany;
              setViewCompany(null);
              if (toEdit) setEditCompany({ ...toEdit });
            }}
          >
            Edit
          </Button>
        </DialogActions>
      </Dialog>

      {/* DELETE / DEACTIVATE CONFIRMATION DIALOG */}
      <Dialog
        open={Boolean(deleteCompany)}
        onClose={() => !deleting && setDeleteCompany(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: tokens.radius.md },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight={700}>
            Deactivate Company
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to deactivate{" "}
            <strong>{deleteCompany?.name}</strong>? Users belonging to this
            tenant will not be able to log in while the company is inactive.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteCompany(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleting}
            startIcon={
              deleting ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <DeleteIcon />
              )
            }
          >
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompaniesPage;
